import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, comics } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const activeCat = categories.find(c => c.slug === activeSlug) || categories[0];
  const filteredComics = activeSlug === 'all'
    ? comics
    : comics.filter(c => c.category === activeSlug);

  const breadcrumbItems = [
    { name: '首页', href: '/' },
    { name: `${activeCat.name}韩漫` },
  ];

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `${activeCat.name}韩漫 - 韩国漫画网`,
    'description': `韩国漫画网提供最全的${activeCat.name}韩漫资源，免费在线观看。${activeCat.description}`,
    'url': `https://www.koreamanhua.com/category/${activeSlug}`,
    'inLanguage': 'zh-Hans',
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩国漫画网',
      'url': 'https://www.koreamanhua.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionPageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="py-6 md:py-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                {activeCat.name}韩漫
              </h1>
              <p className="text-sm text-foreground-500">{activeCat.description}</p>
            </div>

            <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-12">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden transition-all hover:bg-background-200/70 cursor-pointer"
                  >
                    <div className="relative w-full aspect-[3/4] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - ${comic.tags.join('、')}韩国漫画`}
                        title={`${comic.title} - 韩国漫画免费在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md font-medium">
                        {comic.rating}
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{comic.author} · {comic.chapters}话</p>
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        {comic.tags.map((tag) => (
                          <span key={tag} className="text-[11px] text-foreground-400 bg-background-200/70 px-1.5 py-0.5 rounded whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-sm">该分类暂无漫画作品，请浏览其他分类</p>
                <Link to="/category/all" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium">
                  查看全部漫画
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}